import { type VariantProps, cva } from "class-variance-authority";
import clsx from "clsx";
import { ToastContainer as ToastifyToastContainer, type TypeOptions as ToastifyVariants } from "react-toastify";

import { TextButton } from "@/components/ui/buttons/TextButton/TextButton";
import { Loader } from "@/components/ui/status/Loader/Loader";
import { type StatusParams, statusIcon, statusSeparator } from "@/components/ui/status/shared/status";
import { Typography } from "@/components/ui/text/Typography/Typography";

const toastContainer = cva("m-8 w-auto p-0", {
  variants: {
    position: {
      "bottom-right": "right-0 bottom-0",
      "bottom-left": "bottom-0 left-0",
      "top-right": "top-0 right-0",
      "top-left": "top-0 left-0",
      "bottom-center": "bottom-0",
      "top-center": "top-0",
    },
  },
});

const toastWrapper = cva(
  "min-h-0 w-auto max-w-toast overflow-hidden rounded-sm border bg-elevation-background p-0 shadow-4",
  {
    variants: {
      variant: {
        neutral: "border-elevation-outline-1",
        success: "!bg-gradients-gradient-color-200 border-info-success-outline",
        warning: "border-info-warning-outline",
        error: "border-info-error-outline",
      },
      position: {
        "bottom-right": "mt-8",
        "bottom-left": "mt-8",
        "bottom-center": "mt-8",
        "top-right": "mb-8",
        "top-left": "mb-8",
        "top-center": "mb-8",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

type ToastVariantProps = VariantProps<typeof toastWrapper>;

export type ToastPosition = ToastVariantProps["position"];

type ToastProps = ToastVariantProps & StatusParams;

export const Toast = ({ variant, text, isLoading = false, actions = [], icon: Icon }: ToastProps) => {
  return (
    <div className="flex w-auto t:flex-row flex-col items-center">
      <div className="flex items-center gap-3 px-4 py-3-5">
        {isLoading && (
          <Loader
            size="default"
            className="flex-shrink-0"
          />
        )}
        {!isLoading && Icon && <Icon className={statusIcon({ variant })} />}
        <Typography
          size="label-4"
          className={clsx(
            "font-body-prominent-1",
            variant === "error" || variant === "neutral" ? "text-text-default-primary" : "text-text-inverted-primary",
            variant === "success" && "text-text-inverted-primary",
          )}
        >
          {text}
        </Typography>
      </div>
      {actions.length > 0 && (
        <>
          <div className={statusSeparator({ variant })} />
          <div className="flex t:w-auto w-full flex-shrink-0 flex-wrap items-center justify-end t:justify-center gap-4 px-4 py-3">
            {actions.map(({ text: buttonText, onPress, className }) => (
              <TextButton
                size="label-4"
                key={buttonText}
                onPress={onPress}
                color="secondary"
                className={className}
              >
                {buttonText}
              </TextButton>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const toastifyVariantToVariant: Record<ToastifyVariants, ToastVariantProps["variant"]> = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "neutral",
  default: "neutral",
};

export const ToastContainer = () => {
  return (
    <ToastifyToastContainer
      className={(params) => {
        return toastContainer({
          position: params?.position,
          className: params?.defaultClassName,
        });
      }}
      toastClassName={(params) => {
        const variantParam: ToastifyVariants = params?.type || "default";
        const variant = toastifyVariantToVariant[variantParam];
        return toastWrapper({
          variant,
          position: params?.position,
          className: params?.defaultClassName,
        });
      }}
      autoClose={2500}
      icon={false}
      closeButton={false}
      hideProgressBar
    />
  );
};
